/*
 * Signature World — one of the six worlds, Night, or Soulumo Originals.
 * Same navy universe; only the orb's glow changes per world.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import SoulumoHeader from "../../components/SoulumoHeader";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { spaceById, groupBySub } from "./signatureWorlds";
import { TOK, SERIF, SANS, Orb, SectionLabel, InstallRow, NAVY_BG } from "./SignatureBits";
import { DetailSheet, useBlueprintPlay } from "./SignatureCategoryPage";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });

const SubChips = ({ groups, active, onPick, accent }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 mb-8 -mx-5 px-5" style={{ scrollbarWidth: "none" }} data-testid="world-sub-chips">
    {[{ sub: { id: "all", label: "All" } }, ...groups].map(({ sub }) => {
      const on = active === sub.id;
      return (
        <button key={sub.id} type="button" onClick={() => onPick(sub.id)} data-testid={`world-chip-${sub.id}`}
          className="shrink-0 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em] transition-[background-color,border-color,color] duration-200"
          style={{ fontFamily: SANS, border: `1px solid ${on ? accent : TOK.goldMist}`, background: on ? `${accent}1f` : "transparent", color: on ? TOK.cream : TOK.creamDim }}>
          {sub.label}
        </button>
      );
    })}
  </div>
);

export default function SignatureWorldPage() {
  const nav = useNavigate();
  const { worldId } = useParams();
  const space = spaceById(worldId);
  const [items, setItems] = useState(null);
  const [active, setActive] = useState("all");
  const [openBp, setOpenBp] = useState(null);
  const { cloning, cloneError, setCloneError, onPlayNow, added } = useBlueprintPlay();
  const setFav = (id, on) => {
    setItems((xs) => (xs || []).map((x) => (x.id === id ? { ...x, favourite: on } : x)));
    setOpenBp((b) => (b && b.id === id ? { ...b, favourite: on } : b));
  };

  useEffect(() => {
    axios.get(`${API}/api/audio-journeys/blueprints`, auth())
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  const groups = useMemo(() => (space && items ? groupBySub(space, items) : []), [space, items]);
  if (!space) return null;
  const shown = active === "all" ? groups : groups.filter((g) => g.sub.id === active);
  const multi = groups.length > 1;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: NAVY_BG, backgroundAttachment: "fixed", color: TOK.cream, fontFamily: SANS }} data-testid={`signature-world-${space.id}`}>
      <SoulumoHeader />
      <Orb gradient={space.orb} size={440} className="absolute -top-44 -right-40 sig-fade-in" style={{ opacity: 0.8 }} testId="world-orb" />
      <div className="relative max-w-2xl mx-auto px-5 pt-5 pb-28">
        <button onClick={() => nav("/signature-installations")} data-testid="world-back-btn" aria-label="Back"
          className="inline-flex items-center justify-center rounded-full" style={{ width: 44, height: 44, background: "rgba(13,31,58,0.4)", border: `1px solid ${TOK.goldMist}`, color: TOK.cream, backdropFilter: "blur(12px)" }}>
          <ChevronLeft size={18} />
        </button>
        <header className="mt-10 mb-10 sig-rise">
          <div className="text-[0.6rem] uppercase tracking-[0.34em] mb-3" style={{ color: space.accent }}>Signature Installations</div>
          <h1 className="text-4xl sm:text-5xl font-light leading-[1.05]" style={{ fontFamily: SERIF, color: TOK.cream }} data-testid="world-title">{space.label}</h1>
          <p className="mt-3 text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>{space.tagline}</p>
        </header>

        {!items && <div className="flex justify-center py-16"><Loader2 className="animate-spin" color={TOK.gold} /></div>}
        {items && multi && <SubChips groups={groups} active={active} onPick={setActive} accent={space.accent} />}
        {shown.map(({ sub, rows }) => (
          <section key={sub.id} className="mb-10 sig-rise" data-testid={`world-section-${sub.id}`}>
            {multi && <SectionLabel accent={space.accent}>{sub.label}</SectionLabel>}
            {rows.map((bp) => <InstallRow key={bp.id} bp={bp} accent={space.accent} onOpen={() => setOpenBp(bp)} onFav={setFav} />)}
          </section>
        ))}
      </div>
      {openBp && (
        <DetailSheet
          bp={openBp}
          aura={space.accent}
          onClose={() => { if (!cloning) { setOpenBp(null); setCloneError(null); } }}
          onPlayNow={(voice) => onPlayNow(openBp, voice)}
          onTweak={() => nav(`/my-journeys?blueprint=${openBp.id}`)}
          cloning={cloning === openBp.id}
          cloneError={cloneError}
          addedId={added[openBp.id]}
          onFav={setFav}
        />
      )}
    </div>
  );
}
